import { AlertTriangle } from '@untitledui/icons';
import { type ReactNode, useCallback, useMemo } from 'react';
import {
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components';

import { useStackingZIndex } from '@/contexts/StackingContext';
import { Button } from '@/lib/base/buttons/button';
import { Dialog, ModalStateContext } from '@/lib/base/overlays/modal';
import { cx } from '@/utils/cx';

/**
 * For destructive variants, autoFocus is on the Cancel button (safety default).
 * For default/warning variants, autoFocus is on the Confirm button.
 *
 * ConfirmationDialog is a centered, all-corner-rounded confirmation modal.
 * It composes the shared `Dialog` slot API — Dialog owns the header (title,
 * description, optional variant icon), the scrollable body region, and the
 * sticky footer. Consumers configure content via props only.
 */
export interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  description?: string;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive' | 'warning';
  isLoading?: boolean;
  /** When true, disables the Confirm button independently of isLoading. Cancel is unaffected by this prop (it may still be disabled while loading). Defaults to false. */
  isConfirmDisabled?: boolean;
  /** When true, keeps the confirm button's label text visible alongside the loading spinner. Defaults to false. */
  showConfirmTextWhileLoading?: boolean;
  /** When true, the Cancel button and overlay dismiss remain enabled during loading. Defaults to false. */
  keepCancelEnabledWhileLoading?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  /**
   * Optional semantic instance name (e.g. "delete-contact",
   * "unsaved-changes"). Forwarded to the underlying Dialog as
   * `data-instance` for e2e selectors. Format: `<action>-<entity>`
   * (kebab-case).
   */
  instance?: string;
  onClose?: () => void;
}

// =============================================================================
// Static class names — hoisted so they don't reallocate per render.
// =============================================================================

const OVERLAY_CLASS =
  'fixed inset-0 flex items-center justify-center bg-black/50 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200';

const MODAL_CLASS =
  'relative max-w-lg w-full mx-4 max-h-[90dvh] flex flex-col bg-bg-primary rounded-xl shadow-2xl overflow-hidden motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-4 motion-safe:duration-200';

const ICON_BUBBLE_BASE_CLASS =
  'flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center';
const ICON_GLYPH_CLASS = 'w-6 h-6';

const FOOTER_BUTTONS_CLASS = 'flex items-center justify-end gap-3';

const ICON_BG_BY_VARIANT: Record<
  'default' | 'destructive' | 'warning',
  string | null
> = {
  default: null,
  destructive: 'bg-error-50',
  warning: 'bg-warning-50',
};

const ICON_FG_BY_VARIANT: Record<
  'default' | 'destructive' | 'warning',
  string | null
> = {
  default: null,
  destructive: 'text-error-600',
  warning: 'text-warning-600',
};

export function ConfirmationDialog({
  isOpen,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  isLoading = false,
  isConfirmDisabled = false,
  showConfirmTextWhileLoading = false,
  keepCancelEnabledWhileLoading = false,
  onConfirm,
  onCancel,
  instance,
  onClose,
}: ConfirmationDialogProps) {
  const zIndex = useStackingZIndex();

  const handleConfirm = useCallback(async () => {
    try {
      await onConfirm();
      onClose?.();
    } catch {
      // stay open on error
    }
  }, [onConfirm, onClose]);

  const handleCancel = useCallback(() => {
    onCancel();
    onClose?.();
  }, [onCancel, onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleCancel();
      }
    },
    [handleCancel]
  );

  // Style the overlay's flex container only — consumers don't see this.
  const overlayStyle = useMemo(() => ({ zIndex }), [zIndex]);
  const modalStyle = useMemo(() => ({ zIndex: zIndex + 10 }), [zIndex]);

  // Variant icon bubble. Gated on `isOpen` so AlertTriangle isn't dereferenced
  // when the dialog is mounted-but-hidden (preserves the lazy-evaluation
  // semantics tests with strict @untitledui/icons mocks rely on).
  const headerIcon = useMemo(() => {
    if (!isOpen) {
      return undefined;
    }
    if (variant !== 'destructive' && variant !== 'warning') {
      return undefined;
    }
    return (
      <div className={cx(ICON_BUBBLE_BASE_CLASS, ICON_BG_BY_VARIANT[variant])}>
        <AlertTriangle
          className={cx(ICON_GLYPH_CLASS, ICON_FG_BY_VARIANT[variant])}
          aria-hidden='true'
          data-testid='variant-icon'
        />
      </div>
    );
  }, [isOpen, variant]);

  const footer = useMemo(
    () => (
      <div className={FOOTER_BUTTONS_CLASS}>
        <Button
          color='secondary'
          size='md'
          onClick={handleCancel}
          isDisabled={isLoading && !keepCancelEnabledWhileLoading}
          autoFocus={variant === 'destructive'}
          data-testid='confirmation-dialog-cancel'
        >
          {cancelLabel}
        </Button>
        <Button
          color={variant === 'destructive' ? 'primary-destructive' : 'primary'}
          size='md'
          onClick={handleConfirm}
          isDisabled={isConfirmDisabled || isLoading}
          isLoading={isLoading}
          showTextWhileLoading={showConfirmTextWhileLoading}
          autoFocus={variant !== 'destructive'}
          data-testid='confirmation-dialog-confirm'
        >
          {confirmLabel}
        </Button>
      </div>
    ),
    [
      cancelLabel,
      confirmLabel,
      handleCancel,
      handleConfirm,
      isConfirmDisabled,
      isLoading,
      keepCancelEnabledWhileLoading,
      showConfirmTextWhileLoading,
      variant,
    ]
  );

  return (
    <AriaModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={!isLoading || keepCancelEnabledWhileLoading}
      isKeyboardDismissDisabled={isLoading && !keepCancelEnabledWhileLoading}
      className={OVERLAY_CLASS}
      style={overlayStyle}
    >
      <AriaModal className={MODAL_CLASS} style={modalStyle}>
        {state => (
          <ModalStateContext.Provider
            value={{ isEntering: state.isEntering, isExiting: state.isExiting }}
          >
            <Dialog
              data-component='confirmation-dialog'
              instance={instance}
              hideCloseButton
              title={title}
              description={description}
              headerIcon={headerIcon}
              footer={footer}
            >
              <div className='text-sm text-fg-secondary'>{children}</div>
            </Dialog>
          </ModalStateContext.Provider>
        )}
      </AriaModal>
    </AriaModalOverlay>
  );
}
